/**
 * AntImageUrlList — Quản lý danh sách URL ảnh phân bổ vào N field cố định trên 1 record.
 *
 * Concept: BE table có N field độc lập (vd Item.media_url_1..media_url_10).
 * UI hiển thị thành 1 list compact KHÔNG có gap; thêm/xoá/reorder = remap ngược về N field
 * theo thứ tự sourceFields. Slot dư → '' (empty).
 *
 * Props:
 *   value: object                  — toàn bộ form data (chứa các source fields)
 *   onChange: function(patch)      — patch = { [fieldA]: url, [fieldB]: '', ... }
 *   sourceFields: string[]         — vd ['media_url_1', 'media_url_2', ..., 'media_url_10']
 *   voucherModule: string          — vd 'master'
 *   voucherType: string            — vd 'Item'
 *   voucherID: number              — id record (BẮT BUỘC để BE update field)
 *   voucherCode: string (optional)
 *   readOnly: boolean
 *   label: string|object
 *   columns: number                — số ảnh per row (mặc định 5)
 *   thumbSize: number              — kích thước thumbnail px (mặc định 90)
 *
 * Upload: POST /{voucherModule}/{voucherType}/uploadAvatar với target_field = field DB tương ứng.
 * Reorder: chỉ FE shift trong list compact, sau đó save bằng cách patch lại tất cả sourceFields.
 */
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { ListManager } from 'react-beautiful-dnd-grid';
import AntAPI from '../../utils/AntAPI';
import AntLocalization from '../../utils/AntLocalization';
import './index.scss';

export default function AntImageUrlList(props) {
    var onChange = props.onChange;
    var sourceFields = useMemo(function () { return props.sourceFields || []; }, [props.sourceFields]);
    var readOnly = !!props.readOnly;
    var columns = props.columns || 5;
    var thumbSize = props.thumbSize || 90;
    var maxSlots = sourceFields.length;

    var fileInputRef = useRef(null);
    var [uploading, setUploading] = useState(false);
    var [dragOver, setDragOver] = useState(false);
    var [errorMsg, setErrorMsg] = useState('');

    // Internal state: list URLs compact, không phụ thuộc parent re-render.
    var extractUrls = useCallback(function (formObj) {
        var arr = [];
        sourceFields.forEach(function (f) {
            var u = formObj && formObj[f];
            if (u) arr.push(u);
        });
        return arr;
    }, [sourceFields]);

    var [urls, setUrls] = useState(function () { return extractUrls(props.value); });

    // Sync khi props.value thay đổi từ ngoài (load record lần đầu, parent reset).
    useEffect(function () {
        setUrls(extractUrls(props.value));
    }, [props.value, extractUrls]);

    var items = useMemo(function () {
        return urls.map(function (u, idx) { return { url: u, key: 'slot__' + idx + '__' + u }; });
    }, [urls]);

    var currentUrls = urls;

    // Patch: cho 1 mảng URLs sau khi sửa (reorder/add/remove) → cập nhật state nội bộ + patch parent.
    var commitUrls = useCallback(function (newUrls) {
        setUrls(newUrls);
        var patch = {};
        sourceFields.forEach(function (f, idx) {
            patch[f] = newUrls[idx] || '';
        });
        if (onChange) onChange(patch);
    }, [sourceFields, onChange]);

    // Reorder thông qua react-beautiful-dnd-grid.
    var handleDragEnd = useCallback(function (sourceIndex, destinyIndex) {
        if (readOnly) return;
        if (sourceIndex === destinyIndex) return;
        var arr = currentUrls.slice();
        var moved = arr.splice(sourceIndex, 1)[0];
        arr.splice(destinyIndex, 0, moved);
        commitUrls(arr);
    }, [readOnly, currentUrls, commitUrls]);

    var handleRemove = useCallback(function (index) {
        if (readOnly) return;
        var arr = currentUrls.slice();
        arr.splice(index, 1);
        commitUrls(arr);
    }, [readOnly, currentUrls, commitUrls]);

    // Upload tuần tự nhiều file. Mỗi file lấy slot trống tiếp theo theo sourceFields.
    var doUpload = useCallback(async function (files) {
        if (!files || files.length === 0) return;
        if (!props.voucherID) {
            setErrorMsg(AntLocalization.translateText({
                vi: 'Vui lòng lưu sản phẩm trước khi tải ảnh',
                en: 'Please save the product before uploading images',
                fr: 'Enregistrez le produit avant d\'importer des images',
                es: 'Guarde el producto antes de subir imágenes',
                id: 'Simpan produk sebelum mengunggah gambar',
                ja: '画像をアップロードする前に商品を保存してください',
                zh: '请先保存商品再上传图片',
                ko: '이미지 업로드 전에 상품을 저장하세요',
                hi: 'चित्र अपलोड करने से पहले उत्पाद सहेजें',
                ar: 'يرجى حفظ المنتج قبل رفع الصور'
            }));
            return;
        }
        if (!props.voucherModule || !props.voucherType) {
            setErrorMsg('Missing voucherModule / voucherType');
            return;
        }

        var existingUrls = currentUrls.slice();
        var availableSlots = maxSlots - existingUrls.length;
        var fileArr = Array.from(files).filter(function (f) { return /^image\//.test(f.type); });

        if (fileArr.length === 0) {
            setErrorMsg(AntLocalization.translateText({
                vi: 'Chỉ chấp nhận tệp hình ảnh',
                en: 'Only image files are accepted',
                fr: 'Fichiers image uniquement',
                es: 'Solo se aceptan imágenes',
                id: 'Hanya menerima file gambar',
                ja: '画像ファイルのみ対応',
                zh: '仅支持图片文件',
                ko: '이미지 파일만 가능합니다',
                hi: 'केवल चित्र फ़ाइलें स्वीकार्य',
                ar: 'تُقبل ملفات الصور فقط'
            }));
            return;
        }
        if (availableSlots <= 0) {
            setErrorMsg(AntLocalization.translateText({
                vi: 'Đã đạt số lượng ảnh tối đa (' + maxSlots + ')',
                en: 'Maximum number of images reached (' + maxSlots + ')'
            }));
            return;
        }
        if (fileArr.length > availableSlots) {
            fileArr = fileArr.slice(0, availableSlots);
        }

        setErrorMsg('');
        setUploading(true);

        var api = new AntAPI(props.voucherModule, props.voucherType);
        var uploadOne = function (file, targetField) {
            return new Promise(function (resolve) {
                var formData = new FormData();
                formData.append('files', file);
                formData.append('target_field', targetField);
                if (props.voucherCode) formData.append('code', props.voucherCode);

                api.upload('/' + api.routeURL + '/uploadAvatar', formData, function (result) {
                    // Response giống actionUpdatePrepare: {code, data: {full record}}
                    if (result && !result.error && result.data) {
                        resolve(result);
                    } else {
                        resolve(null);
                    }
                });
            });
        };

        try {
            var lastResult = null;
            for (var i = 0; i < fileArr.length; i++) {
                var slotIdx = existingUrls.length + i;
                var targetField = sourceFields[slotIdx];
                var r = await uploadOne(fileArr[i], targetField);
                if (r) lastResult = r;
            }
            if (lastResult && lastResult.data) {
                // Lấy list URLs cuối cùng từ record để re-render component + báo lên AntVoucher
                var finalUrls = extractUrls(lastResult.data);
                setUrls(finalUrls);
                if (props.onUploaded) props.onUploaded(lastResult);
            }
        } catch (e) {
            setErrorMsg(AntLocalization.translateText({ vi: 'Tải ảnh thất bại', en: 'Upload failed', fr: 'Échec de l\'envoi', es: 'Error al subir', id: 'Gagal mengunggah', ja: 'アップロード失敗', zh: '上传失败', ko: '업로드 실패', hi: 'अपलोड विफल', ar: 'فشل الرفع' }));
        }
        setUploading(false);
    }, [props.voucherID, props.voucherCode, props.voucherModule, props.voucherType, sourceFields, maxSlots, currentUrls, extractUrls, props.onUploaded]);

    var handleSelect = useCallback(function () {
        if (fileInputRef.current) fileInputRef.current.click();
    }, []);

    var handleFileChange = useCallback(function (e) {
        doUpload(e.target.files);
        e.target.value = '';
    }, [doUpload]);

    var handleDragOver = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!readOnly) setDragOver(true);
    }, [readOnly]);

    var handleDragLeave = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    }, []);

    var handleDrop = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (readOnly) return;
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            doUpload(e.dataTransfer.files);
        }
    }, [readOnly, doUpload]);

    var renderThumb = useCallback(function (item) {
        var index = items.indexOf(item);
        return (
            <div
                className="ant-image-url-list-item"
                style={{ width: thumbSize, height: thumbSize }}
                key={item.key}
            >
                <img className="ant-image-url-list-thumb" src={item.url} alt="" />
                {!readOnly && (
                    <button
                        className="ant-image-url-list-remove"
                        type="button"
                        onMouseDown={function (e) { e.stopPropagation(); }}
                        onPointerDown={function (e) { e.stopPropagation(); }}
                        onClick={function (e) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemove(index);
                        }}
                        title={AntLocalization.translateText({ vi: 'Xoá', en: 'Remove', fr: 'Retirer', es: 'Quitar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएँ', ar: 'حذف' })}
                    >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>
        );
    }, [items, thumbSize, readOnly, handleRemove]);

    var canAddMore = items.length < maxSlots;

    return (
        <div className="ant-image-url-list">
            <div className="ant-image-url-list-count">{items.length}/{maxSlots}</div>
            <div
                className={'ant-image-url-list-zone' + (dragOver ? ' is-drag' : '')}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {items.length === 0 ? (
                    <div
                        className="ant-image-url-list-empty"
                        onClick={readOnly || uploading ? undefined : handleSelect}
                    >
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M21 15l-5-5L5 21" />
                        </svg>
                        <div>
                            {AntLocalization.translateText({
                                vi: 'Kéo ảnh vào đây hoặc click để chọn',
                                en: 'Drag images here or click to select',
                                fr: 'Glissez les images ici ou cliquez',
                                es: 'Arrastre imágenes aquí o haga clic',
                                id: 'Seret gambar ke sini atau klik',
                                ja: '画像をドラッグ、またはクリックして選択',
                                zh: '将图片拖到此处或点击选择',
                                ko: '이미지를 끌어오거나 클릭하여 선택',
                                hi: 'चित्र यहाँ खींचें या चुनने हेतु क्लिक करें',
                                ar: 'اسحب الصور هنا أو انقر للاختيار'
                            })}
                        </div>
                    </div>
                ) : (
                    <div className="ant-image-url-list-grid" style={{ '--cols': columns }}>
                        <ListManager
                            items={items}
                            direction="horizontal"
                            maxItems={columns}
                            render={renderThumb}
                            onDragEnd={handleDragEnd}
                        />
                        {!readOnly && canAddMore && (
                            <button
                                type="button"
                                className="ant-image-url-list-add"
                                style={{ width: thumbSize, height: thumbSize }}
                                onClick={uploading ? undefined : handleSelect}
                                disabled={uploading}
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 5v14M5 12h14" />
                                </svg>
                                <span>{AntLocalization.translateText({ vi: 'Thêm ảnh', en: 'Add image', fr: 'Ajouter', es: 'Añadir', id: 'Tambah', ja: '画像を追加', zh: '添加图片', ko: '이미지 추가', hi: 'चित्र जोड़ें', ar: 'إضافة صورة' })}</span>
                            </button>
                        )}
                    </div>
                )}

                {uploading && (
                    <div className="ant-image-url-list-overlay">
                        <div className="ant-image-url-list-spinner" />
                        <div>{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Uploading...', fr: 'Envoi...', es: 'Subiendo...', id: 'Mengunggah...', ja: '送信中...', zh: '上传中...', ko: '업로드 중...', hi: 'अपलोड हो रहा...', ar: 'جارٍ الرفع...' })}</div>
                    </div>
                )}
            </div>

            {!readOnly && (
                <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFileChange} />
            )}

            {errorMsg && <div className="ant-image-url-list-error">{errorMsg}</div>}
        </div>
    );
}
